import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BatchFileUploadModal, MobileCameraUploadModal } from '@hofsync/design-system';

interface UploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: File[], comment: string) => Promise<void>;
}

// Hook to detect mobile viewport
function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        // Fallback for test environments without matchMedia
        if (typeof window.matchMedia !== 'function') return false;
        return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
    });

    useEffect(() => {
        // Skip in test environments without matchMedia
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setIsMobile(e.matches);
        };

        // Set initial value
        handleChange(mediaQuery);

        // Listen for changes
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [breakpoint]);

    return isMobile;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUpload }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    // Wrapper for single file upload (mobile camera)
    const handleSingleFileUpload = async (file: File, comment: string) => {
        await onUpload([file], comment);
    };

    // On mobile, show the camera upload modal
    if (isMobile) {
        return (
            <MobileCameraUploadModal
                isOpen={isOpen}
                onClose={onClose}
                onUpload={handleSingleFileUpload}
                title={t('documents.camera.title')}
                cameraButtonText={t('documents.camera.takePhoto')}
                commentLabel={t('documents.comment')}
                commentPlaceholder={t('documents.commentPlaceholder')}
                cancelText={t('common.cancel')}
                uploadText={t('documents.upload')}
                uploadingText={t('documents.uploading')}
                successMessage={t('documents.camera.uploadSuccess')}
                closeOnSuccess={true}
            />
        );
    }

    // On desktop, show the batch file upload modal
    return (
        <BatchFileUploadModal
            isOpen={isOpen}
            onClose={onClose}
            onUpload={onUpload}
            title={t('documents.batch.title')}
            dropText={t('documents.batch.dropText')}
            allowedTypesText={t('documents.allowedTypes')}
            commentLabel={t('documents.comment')}
            commentPlaceholder={t('documents.commentPlaceholder')}
            cancelText={t('common.cancel')}
            uploadText={t('documents.upload')}
            uploadingText={t('documents.uploading')}
            successMessage={t('documents.batch.uploadSuccess')}
            closeOnSuccess={true}
            filesSelectedText={t('documents.batch.filesSelected')}
            clearAllText={t('documents.batch.clearAll')}
            removeFileLabel={t('documents.batch.removeFile')}
            addMoreText={t('documents.batch.addMore')}
            partialSuccessMessage={t('documents.batch.partialSuccess')}
        />
    );
};

export default UploadModal;
